import { IGrid } from "../types/IGrid";
import { IPoint } from "../types/IPoint";
import { IStructure } from "../types/IStructure";
import { PolygonUtils } from "./PolygonUtils";
import { UnionFind } from "./UnionFind";

const GRID_DISTANCE_THRESHOLD = 300; // meters

/**
 * Get the centroid of a structure.
 * For circles, returns the center point.
 * For polygons, returns the centroid of the polygon.
 */
const getStructureCentroid = (structure: IStructure): IPoint => {
  if (structure.type === 'circle') {
    return structure.point;
  } else {
    return PolygonUtils.getCentroid(structure.points);
  }
};

/**
 * Calculate grids from structures.
 * Structures within GRID_DISTANCE_THRESHOLD meters of each other are grouped together.
 */
const calculateGrids = (structures: IStructure[]): IGrid[] => {
  if (structures.length === 0) return [];

  // Compute centroids for all structures
  const centroids = structures.map(s => getStructureCentroid(s));

  // Initialize Union-Find
  const uf = new UnionFind(structures.length);

  // For each pair of structures, union them if they're within threshold distance
  for (let i = 0; i < structures.length; i++) {
    for (let j = i + 1; j < structures.length; j++) {
      const distance = PolygonUtils.distance(centroids[i], centroids[j]);
      if (distance <= GRID_DISTANCE_THRESHOLD) {
        uf.union(i, j);
      }
    }
  }

  // Group structures by index, then map to IGrid
  const groups = uf.getGroups();
  return groups.map(indices => ({
    structures: indices.map(i => structures[i])
  }));
};

export { getStructureCentroid, calculateGrids, GRID_DISTANCE_THRESHOLD };
